import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Download, IndianRupee, TrendingDown, PiggyBank, Paperclip, X, Loader2, FlaskConical, Banknote, ShoppingCart } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useMode } from "../../context/ModeContext";
import { inputCls, PageHeader } from "./shared";
import { FinanceDrawings } from "./FinanceDrawings";
import { FinanceUdhaar } from "./FinanceUdhaar";
import { FinanceCards } from "./FinanceCards";

const OPERATIONAL_CATEGORIES = ["Shop Rent", "Staff Salaries", "Electricity & Bills", "Water", "Salon Refreshments", "Maintenance / Repairs", "Marketing & Ads", "General Misc"];
const PERSONAL_CHIPS = ["Bank EMI / Installment", "Car / Bike Petrol", "Kids School / Needs", "Home Ration", "Family Personal"];
const SOURCE_ACCOUNTS = ["Salon Cash Drawer (Counter)", "Salon Business Bank Account", "Papa Ji's Account / UPI", "Mummy Ji's Account / UPI", "Personal Self Account / UPI"];
const FAMILY_SOURCES = ["Papa Ji's Account / UPI", "Mummy Ji's Account / UPI"];
const todayStr = () => new Date().toISOString().split("T")[0];
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const emptyExp = (type = "operational") => ({
  date: todayStr(), category: type === "personal" ? "" : "Shop Rent", expense_type: type, description: "", amount: "",
  payment_method: "Cash", cheque_no: "", cheque_date: "", receipt_path: "",
  source_account: SOURCE_ACCOUNTS[0], scheduled_date: "", credit_card_id: "",
});

const compressImage = (file) => new Promise((resolve) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const scale = Math.min(1, 1400 / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    canvas.toBlob((blob) => resolve(blob || file), "image/jpeg", 0.8);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;
const TICK_AXIS = { fontSize: 12, fill: "#5C6A63" };
const BAR_RADIUS = [4, 4, 0, 0];

export default function Finance() {
  const { viewMode } = useMode();
  const isCeo = viewMode !== "manager";
  const [tab, setTab] = useState("expenses");
  const [range, setRange] = useState({ preset: "month", from: monthStart(), to: todayStr() });
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [section, setSection] = useState("expenses");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyExp());
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [cardsList, setCardsList] = useState([]);

  const load = useCallback(() => {
    api.get("/expenses", { params: { from_date: range.from, to_date: range.to } }).then(({ data }) => setExpenses(data)).catch(() => {});
    api.get("/credit-cards").then(({ data }) => setCardsList(data.rows || [])).catch(() => {});
    if (!isCeo) return;
    api.get("/finance/summary", { params: { from_date: range.from, to_date: range.to } }).then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/finance/vendor-ledger").then(({ data }) => setLedger(data)).catch(() => {});
  }, [range.from, range.to, isCeo]);
  useEffect(() => { load(); }, [load]);

  const setPreset = (preset) => {
    const now = new Date();
    if (preset === "month") setRange({ preset, from: monthStart(), to: todayStr() });
    if (preset === "lastmonth") {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      setRange({ preset, from: s.toLocaleDateString("en-CA"), to: e.toLocaleDateString("en-CA") });
    }
    if (preset === "year") setRange({ preset, from: `${now.getFullYear()}-01-01`, to: todayStr() });
  };

  const uploadReceipt = async (file) => {
    setUploading(true);
    try {
      const blob = await compressImage(file);
      const fd = new FormData();
      fd.append("file", blob, "receipt.jpg");
      const { data } = await api.post("/uploads/receipt", fd);
      setForm((f) => ({ ...f, receipt_path: data.path }));
      toast.success("Receipt photo attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setUploading(false);
  };

  const viewReceipt = async (e) => {
    try {
      const { data } = await api.get(`/receipts/${e.receipt_path}`, { responseType: "blob" });
      setViewing({ expense: e, url: URL.createObjectURL(data) });
    } catch (err) {
      toast.error("Could not load the receipt photo");
    }
  };

  const closeViewer = () => {
    if (viewing?.url) URL.revokeObjectURL(viewing.url);
    setViewing(null);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      date: e.date, category: e.category, expense_type: e.expense_type || "operational", description: e.description || "",
      amount: String(e.amount), payment_method: e.payment_method, cheque_no: e.cheque_no || "", cheque_date: e.cheque_date || "",
      receipt_path: e.receipt_path || "", source_account: e.source_account || SOURCE_ACCOUNTS[0],
      scheduled_date: e.scheduled_date || "", credit_card_id: e.credit_card_id || "",
    });
    setOpen(true);
  };

  const saveExpense = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    if (!form.category.trim()) { toast.error("Enter a category"); return; }
    const payload = { ...form, amount: Number(form.amount) };
    try {
      if (editing) { await api.put(`/expenses/${editing.id}`, payload); toast.success("Expense updated"); }
      else { await api.post("/expenses", payload); toast.success(form.expense_type === "personal" ? "Personal drawing recorded" : "Expense recorded"); }
      setOpen(false); setEditing(null); setForm(emptyExp()); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removeExpense = async (id) => {
    try { await api.delete(`/expenses/${id}`); toast.success("Expense deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const setCheque = async (exp, status) => {
    try {
      await api.patch(`/expenses/${exp.id}/cheque`, { cheque_status: status });
      toast.success(`Cheque ${exp.cheque_no || ""} marked ${status}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/finance/export", { params: { from_date: range.from, to_date: range.to }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `finance_${range.from}_${range.to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Finance report downloaded");
    } catch (err) { toast.error("Export failed"); }
  };

  const operationalExpenses = expenses.filter((e) => (e.expense_type || "operational") !== "personal");
  const drawings = expenses.filter((e) => e.expense_type === "personal");
  const cheques = expenses.filter((e) => e.payment_method === "Cheque");
  const tableRows = isCeo ? operationalExpenses : expenses;

  const cards = summary ? [
    { label: "Income", value: summary.income.total, icon: IndianRupee, sub: `bills ${fmt(summary.income.bills_collected)} · appts ${fmt(summary.income.appointments)} · packages ${fmt(summary.income.package_sales)}`, color: "text-emerald-700" },
    { label: "Operational Expenses", value: summary.expenses.operational, icon: TrendingDown, sub: "rent, salaries, utilities…", color: "text-red-600" },
    { label: "Products Consumed", value: summary.expenses.products_used || 0, icon: FlaskConical, sub: "backbar + salon usage cost", color: "text-red-600" },
    { label: "Net Profit", value: summary.net_profit, icon: PiggyBank, sub: "income − expenses − consumption ± interest", color: summary.net_profit >= 0 ? "text-[#0A3F2F]" : "text-red-600" },
    { label: "Cash in Hand", value: summary.cash?.in_hand || 0, icon: Banknote, sub: `cash in ${fmt(summary.cash?.collected)} − cash outflows`, color: (summary.cash?.in_hand || 0) >= 0 ? "text-[#0A3F2F]" : "text-red-600" },
    { label: "Purchases Paid", value: summary.expenses.purchases_paid, icon: ShoppingCart, sub: "supplier payments (cashflow, not P&L)", color: "text-[#8a6d33]" },
  ] : [];

  const TABS = [
    ["expenses", "Expenses & Cashflow"],
    ...(isCeo ? [["drawings", "Personal Drawings"], ["udhaar", "Udhaar Khata"]] : []),
    ["cards", "Credit Cards & Cheques (PDC)"],
  ];

  return (
    <div className="space-y-6" data-testid="finance-page">
      <PageHeader title={isCeo ? "Finance" : "Expenses"} subtitle={isCeo ? "Expenses, drawings, udhaar khata, cards and cheques — all in one place." : "Record salon expenses and track cheques."}
        action={
          <div className="flex gap-2">
            {isCeo && (
            <button onClick={exportCsv} data-testid="finance-export-btn"
              className="bg-white border border-[#E5E7E5] text-[#0A3F2F] text-sm font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 hover:border-[#0A3F2F]">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            )}
            <button onClick={() => { setEditing(null); setForm(emptyExp()); setOpen(true); }} data-testid="add-expense-btn"
              className="bg-[#0A3F2F] text-white text-sm font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 hover:bg-[#0d5540]">
              <Plus className="w-4 h-4" /> Add Expense
            </button>
          </div>
        }
      />

      <div className="flex gap-2 flex-wrap border-b border-[#E5E7E5] pb-3" data-testid="finance-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`finance-tab-${k}`}
            className={`text-xs font-bold px-4 py-2.5 rounded-full transition-colors ${tab === k ? "bg-[#0A3F2F] text-white" : "bg-white border border-[#E5E7E5] text-[#5C6A63] hover:border-[#0A3F2F]"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab !== "udhaar" && (
      <div className="flex flex-wrap items-center gap-3">
        {[["month", "This Month"], ["lastmonth", "Last Month"], ["year", "This Year"]].map(([k, label]) => (
          <button key={k} onClick={() => setPreset(k)} data-testid={`finance-preset-${k}`}
            className={`text-xs font-semibold px-3.5 py-2 rounded-full ${range.preset === k ? "bg-[#C5A059] text-[#1C1C1C]" : "bg-white border border-[#E5E7E5] text-[#5C6A63]"}`}>
            {label}
          </button>
        ))}
        <input data-testid="finance-from-input" type="date" className={`${inputCls} !w-40`} value={range.from} onChange={(e) => setRange({ preset: "custom", from: e.target.value, to: range.to })} />
        <span className="text-xs text-[#5C6A63]">to</span>
        <input data-testid="finance-to-input" type="date" className={`${inputCls} !w-40`} value={range.to} onChange={(e) => setRange({ preset: "custom", from: range.from, to: e.target.value })} />
      </div>
      )}

      {tab === "expenses" && (
      <>
      {isCeo && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={`finance-card-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-wider text-[#5C6A63] font-semibold">{c.label}</p>
              <c.icon className="w-4 h-4 text-[#C5A059]" />
            </div>
            <p className={`text-2xl font-bold ${c.color}`}>{fmt(c.value)}</p>
            <p className="text-[11px] text-[#5C6A63] mt-1">{c.sub}</p>
          </div>
        ))}
      </div>
      )}

      {isCeo && (
      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm">
          <h2 className="text-sm font-bold text-[#1C1C1C] mb-4">Income vs Expense — Last 6 Months</h2>
          <div className="h-64" data-testid="finance-chart">
            {summary && (
              <ResponsiveContainer width="100%" height="100%" minHeight={220}>
                <BarChart data={summary.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7E5" />
                  <XAxis dataKey="label" tick={TICK_AXIS} />
                  <YAxis tick={TICK_AXIS} />
                  <Tooltip formatter={(v, name) => [fmt(v), name === "income" ? "Income" : "Expense"]} />
                  <Legend formatter={(v) => (v === "income" ? "Income" : "Expense")} />
                  <Bar dataKey="income" fill="#0A3F2F" radius={BAR_RADIUS} />
                  <Bar dataKey="expense" fill="#C5A059" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="lg:col-span-2 bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm">
          <h2 className="text-sm font-bold text-[#1C1C1C] mb-4">Operational Expenses by Category</h2>
          <div className="space-y-2" data-testid="category-breakdown">
            {(!summary || summary.by_category.length === 0) && <p className="text-sm text-[#5C6A63]">No expenses in this range.</p>}
            {summary?.by_category.map((c) => (
              <div key={c.category} className="flex items-center justify-between text-sm border-b border-[#E5E7E5] pb-2 last:border-0">
                <span className="text-[#5C6A63]">{c.category}</span>
                <span className="font-semibold text-[#1C1C1C]">{fmt(c.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {[["expenses", `Expenses (${tableRows.length})`], ...(!isCeo ? [["cheques", `Cheques (${cheques.length})`]] : []), ...(isCeo ? [["vendors", `Vendor Ledger (${ledger?.rows?.length || 0})`]] : [])].map(([k, label]) => (
          <button key={k} onClick={() => setSection(k)} data-testid={`finance-section-${k}`}
            className={`text-xs font-semibold px-4 py-2 rounded-full transition-colors ${section === k ? "bg-[#0A3F2F] text-white" : "bg-white border border-[#E5E7E5] text-[#5C6A63] hover:border-[#0A3F2F]"}`}>
            {label}
          </button>
        ))}
      </div>

      {(section === "expenses" || section === "cheques") && (
        <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold">Description</th>
                <th className="px-4 py-3 font-semibold">Paid From</th>
                <th className="px-4 py-3 font-semibold">Method</th>
                <th className="px-4 py-3 font-semibold">Amount</th>
                <th className="px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="expenses-table-body">
              {(section === "cheques" ? cheques : tableRows).length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#5C6A63]" data-testid="no-expenses">No {section === "cheques" ? "cheque payments" : "expenses"} in this range.</td></tr>
              )}
              {(section === "cheques" ? cheques : tableRows).map((e) => (
                <tr key={e.id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`expense-row-${e.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {e.date}
                    {e.scheduled_date && <p className="text-[10px] text-[#8a6d33] font-semibold">next due {e.scheduled_date}</p>}
                  </td>
                  <td className="px-4 py-3 font-semibold">
                    {e.category}
                    {e.expense_type === "personal" && <span className="ml-1.5 text-[10px] font-bold bg-[#C5A059]/20 text-[#8a6d33] px-2 py-0.5 rounded-full">Personal</span>}
                  </td>
                  <td className="px-4 py-3 text-[#5C6A63]">
                    {e.description || "—"}
                    {e.receipt_path && (
                      <button onClick={() => viewReceipt(e)} data-testid={`view-receipt-${e.id}`} title="View receipt photo"
                        className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-[#0A3F2F] bg-[#0A3F2F]/10 px-2 py-0.5 rounded-full hover:bg-[#0A3F2F] hover:text-white transition-colors">
                        <Paperclip className="w-3 h-3" /> receipt
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#5C6A63]">
                    {e.source_account || "Salon Cash Drawer (Counter)"}
                    {FAMILY_SOURCES.includes(e.source_account) && <p className="text-[10px] font-bold text-[#8a6d33]">→ family ledger</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-[#5C6A63]">{e.payment_method}</span>
                    {e.payment_method === "Cheque" && (
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <span data-testid={`cheque-status-${e.id}`} className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${e.cheque_status === "cleared" ? "bg-emerald-600/10 text-emerald-700" : e.cheque_status === "bounced" ? "bg-red-100 text-red-600" : "bg-[#C5A059]/20 text-[#8a6d33]"}`}>
                          {e.cheque_status || "issued"}{e.cheque_no ? ` · #${e.cheque_no}` : ""}
                        </span>
                        {e.cheque_status === "issued" && (
                          <>
                            <button onClick={() => setCheque(e, "cleared")} data-testid={`cheque-clear-${e.id}`} className="text-[10px] font-bold text-emerald-700 underline underline-offset-2">Cleared</button>
                            <button onClick={() => setCheque(e, "bounced")} data-testid={`cheque-bounce-${e.id}`} className="text-[10px] font-bold text-red-600 underline underline-offset-2">Bounced</button>
                          </>
                        )}
                        {e.cheque_status === "bounced" && (
                          <button onClick={() => setCheque(e, "issued")} data-testid={`cheque-reissue-${e.id}`} className="text-[10px] font-bold text-[#8a6d33] underline underline-offset-2">Re-issue</button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className={`px-4 py-3 font-bold ${e.cheque_status === "bounced" ? "text-gray-400 line-through" : "text-red-600"}`}>{fmt(e.amount)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-3">
                      <button onClick={() => openEdit(e)} data-testid={`edit-expense-${e.id}`} className="text-[#3D4A44] hover:text-[#0A3F2F]"><Pencil className="w-4 h-4" /></button>
                      {isCeo && (
                        <button onClick={() => removeExpense(e.id)} data-testid={`delete-expense-${e.id}`} className="text-[#3D4A44] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {section === "vendors" && (
        <div className="space-y-3">
          {ledger && ledger.total_outstanding > 0 && (
            <p className="text-sm font-semibold text-red-600" data-testid="vendor-outstanding">Total outstanding to vendors: {fmt(ledger.total_outstanding)}</p>
          )}
          <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                  <th className="px-4 py-3 font-semibold">Vendor</th>
                  <th className="px-4 py-3 font-semibold">Purchases</th>
                  <th className="px-4 py-3 font-semibold">Total Bought</th>
                  <th className="px-4 py-3 font-semibold">Paid</th>
                  <th className="px-4 py-3 font-semibold">Balance Due</th>
                  <th className="px-4 py-3 font-semibold">Last Purchase</th>
                </tr>
              </thead>
              <tbody data-testid="vendor-ledger-body">
                {(!ledger || ledger.rows.length === 0) && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#5C6A63]" data-testid="no-vendors">No suppliers yet — add them in Inventory.</td></tr>}
                {ledger?.rows.map((v) => (
                  <tr key={v.supplier_id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`vendor-row-${v.supplier_id}`}>
                    <td className="px-4 py-3"><p className="font-semibold">{v.name}</p><p className="text-xs text-[#5C6A63]">{v.phone || "—"}</p></td>
                    <td className="px-4 py-3">{v.purchase_count}</td>
                    <td className="px-4 py-3">{fmt(v.total_purchases)}</td>
                    <td className="px-4 py-3 text-emerald-700 font-semibold">{fmt(v.total_paid)}</td>
                    <td className="px-4 py-3">
                      {v.balance_due > 0 ? (
                        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-600">{fmt(v.balance_due)}</span>
                      ) : (
                        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-600/10 text-emerald-700">Settled</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#5C6A63]">{v.last_purchase}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>
      )}

      {tab === "drawings" && isCeo && (
        <FinanceDrawings drawings={drawings} byPersonal={summary?.by_personal || []} isCeo={isCeo}
          onAdd={() => { setEditing(null); setForm(emptyExp("personal")); setOpen(true); }}
          onEdit={openEdit} onDelete={removeExpense} />
      )}

      {tab === "udhaar" && isCeo && <FinanceUdhaar />}

      {tab === "cards" && <FinanceCards cheques={cheques} onChequeStatus={setCheque} isCeo={isCeo} />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Expense" : form.expense_type === "personal" ? "Add Personal Drawing" : "Add Expense"}</DialogTitle></DialogHeader>
          <form onSubmit={saveExpense} className="space-y-3" data-testid="expense-form">
            <div className="flex items-center gap-5 text-sm font-medium">
              <label className="flex items-center gap-2">
                <input type="radio" name="exp-type" data-testid="expense-type-operational" checked={form.expense_type === "operational"}
                  onChange={() => setForm({ ...form, expense_type: "operational", category: OPERATIONAL_CATEGORIES.includes(form.category) ? form.category : "Shop Rent" })} />
                Operational (salon)
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="exp-type" data-testid="expense-type-personal" checked={form.expense_type === "personal"}
                  onChange={() => setForm({ ...form, expense_type: "personal", category: OPERATIONAL_CATEGORIES.includes(form.category) ? "" : form.category })} />
                Personal drawing
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="expense-date-input" type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              {form.expense_type === "operational" ? (
                <select data-testid="expense-category-select" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {!OPERATIONAL_CATEGORIES.includes(form.category) && form.category && <option>{form.category}</option>}
                  {OPERATIONAL_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              ) : (
                <input data-testid="expense-category-input" className={inputCls} placeholder="Category (e.g. Bank EMI)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              )}
            </div>
            {form.expense_type === "personal" && (
              <div className="flex gap-1.5 flex-wrap" data-testid="personal-chips">
                {PERSONAL_CHIPS.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, category: c })} data-testid={`personal-chip-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`text-[11px] font-semibold px-3 py-1 rounded-full border ${form.category === c ? "bg-[#C5A059] text-[#1C1C1C] border-[#C5A059]" : "bg-white border-[#E5E7E5] text-[#5C6A63] hover:border-[#C5A059]"}`}>
                    {c}
                  </button>
                ))}
              </div>
            )}
            <input data-testid="expense-description-input" className={inputCls} placeholder="Description (e.g. June shop rent)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="expense-amount-input" type="number" min="1" step="any" className={inputCls} placeholder="Amount ₹ *" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              <select data-testid="expense-method-select" className={inputCls} value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                <option>Cash</option><option>UPI</option><option>Card</option><option>Credit Card</option><option>Bank Transfer</option><option>Cheque</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Paid from / source account</label>
              <select data-testid="expense-source-select" className={inputCls} value={form.source_account} onChange={(e) => setForm({ ...form, source_account: e.target.value })}>
                {SOURCE_ACCOUNTS.map((a) => <option key={a}>{a}</option>)}
              </select>
              {FAMILY_SOURCES.includes(form.source_account) && form.expense_type === "operational" && (
                <p className="text-[11px] text-[#8a6d33] mt-1" data-testid="family-hint">Counted as a salon expense, salon counter cash is NOT reduced — the amount is credited to the Family Settlement Ledger.</p>
              )}
            </div>
            {form.payment_method === "Credit Card" && (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Charge to card</label>
                <select data-testid="expense-card-select" className={inputCls} value={form.credit_card_id} onChange={(e) => setForm({ ...form, credit_card_id: e.target.value })}>
                  <option value="">Select card…</option>
                  {cardsList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {cardsList.length === 0 && <p className="text-[11px] text-[#8a6d33] mt-1">No cards saved — add them in the Credit Cards &amp; Cheques tab.</p>}
              </div>
            )}
            {form.payment_method === "Cheque" && (
              <div className="grid grid-cols-2 gap-3 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-3" data-testid="cheque-fields">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Cheque number</label>
                  <input data-testid="expense-cheque-no-input" className={inputCls} placeholder="e.g. 000123" value={form.cheque_no} onChange={(e) => setForm({ ...form, cheque_no: e.target.value })} />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Cheque date</label>
                  <input data-testid="expense-cheque-date-input" type="date" className={inputCls} value={form.cheque_date} onChange={(e) => setForm({ ...form, cheque_date: e.target.value })} />
                </div>
              </div>
            )}
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Scheduled / next due date (optional reminder)</label>
              <input data-testid="expense-scheduled-input" type="date" className={inputCls} value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Receipt / bill photo (optional)</label>
              {form.receipt_path ? (
                <div className="flex items-center justify-between bg-[#0A3F2F]/5 border border-[#0A3F2F]/20 rounded-md px-3 py-2.5 text-sm mt-1" data-testid="receipt-attached">
                  <span className="flex items-center gap-2 font-semibold text-[#0A3F2F]"><Paperclip className="w-4 h-4" /> Photo attached</span>
                  <button type="button" onClick={() => setForm({ ...form, receipt_path: "" })} data-testid="remove-receipt-btn" className="text-gray-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                </div>
              ) : uploading ? (
                <div className="flex items-center gap-2 text-sm text-[#5C6A63] mt-1 py-2" data-testid="receipt-uploading"><Loader2 className="w-4 h-4 animate-spin" /> Uploading photo…</div>
              ) : (
                <input type="file" accept="image/jpeg,image/png,image/webp" data-testid="expense-receipt-input"
                  className="block w-full text-sm text-[#5C6A63] mt-1 file:mr-3 file:rounded-full file:border-0 file:bg-[#0A3F2F] file:px-4 file:py-2 file:text-xs file:font-semibold file:text-white hover:file:bg-[#0d5540]"
                  onChange={(e2) => e2.target.files?.[0] && uploadReceipt(e2.target.files[0])} />
              )}
            </div>
            <button type="submit" data-testid="expense-submit-btn" disabled={uploading} className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540] disabled:opacity-50">
              {editing ? "Save Changes" : form.expense_type === "personal" ? "Record Drawing" : "Record Expense"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={(v) => !v && closeViewer()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Receipt — {viewing?.expense?.category} ₹{viewing?.expense?.amount?.toLocaleString("en-IN")} ({viewing?.expense?.date})</DialogTitle>
          </DialogHeader>
          {viewing && (
            <img src={viewing.url} alt="Expense receipt" data-testid="receipt-image" className="w-full max-h-[70vh] object-contain rounded-md border border-[#E5E7E5]" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
